/** Pure finance rules shared by the API (validation, statuses) and the web app (forms, chips). */

export const INSTALLMENT_STATUSES = ['paid', 'pending', 'late', 'todo'] as const;
export type InstallmentStatus = (typeof INSTALLMENT_STATUSES)[number];

/**
 * Amount typed by a user in dinars ("3 250,000", "3250.5", "3 250") → millimes, or null when invalid.
 * At most 3 decimals; spaces (incl. no-break) are thousands separators; "," or "." is the decimal mark.
 */
export function parseMoney(input: string): number | null {
  const s = input.replace(/[\s\u00a0\u202f]/g, '').replace(/(DT|TND|د\.ت)$/i, '');
  const m = /^(\d{1,9})(?:[.,](\d{1,3}))?$/.exec(s);
  if (!m) return null;
  const millimes = Number(m[1]) * 1000 + Number((m[2] ?? '').padEnd(3, '0') || 0);
  return Number.isSafeInteger(millimes) ? millimes : null;
}

/** Millimes → value for an amount input ("3250,000" → "3 250,000" without the currency). */
export function moneyInput(millimes: number): string {
  const int = Math.floor(millimes / 1000)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${int},${String(millimes % 1000).padStart(3, '0')}`;
}

/** Calendar date ("YYYY-MM-DD") in the institution's time zone. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/** Whole days from `from` to `to` (both "YYYY-MM-DD"); negative when `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/**
 * Installment status from its payments (design ScrFinance chips):
 * paid = cleared payments cover it; pending = covered once pending cheques clear; late = due date passed.
 */
export function installmentStatus(i: {
  amountMillimes: number;
  paidMillimes: number;
  pendingMillimes: number;
  dueOn: string;
  today: string;
}): InstallmentStatus {
  if (i.paidMillimes >= i.amountMillimes) return 'paid';
  if (i.paidMillimes + i.pendingMillimes >= i.amountMillimes) return 'pending';
  return i.dueOn < i.today ? 'late' : 'todo';
}

/** What can still be collected: cleared and pending payments both reduce it. */
export function remainingDue(i: { amountMillimes: number; paidMillimes: number; pendingMillimes: number }) {
  return Math.max(0, i.amountMillimes - i.paidMillimes - i.pendingMillimes);
}

/** "R-2026-00318". */
export function formatReceiptNumber(prefix: string, year: number, seq: number): string {
  return `${prefix}-${year}-${String(seq).padStart(5, '0')}`;
}
